<div class="min-h-screen bg-gray-50">
  <!-- Header -->
  <app-header-nav></app-header-nav>

  <mat-toolbar class="border-b bg-white shadow-sm">
    <button mat-icon-button (click)="onBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h1 class="text-xl font-semibold text-gray-800 ml-4">Order Management</h1>
    <span class="flex-auto"></span>
    <button mat-raised-button color="primary" (click)="refreshOrders()" [disabled]="isLoading">
      <mat-icon>refresh</mat-icon> Refresh
    </button>
  </mat-toolbar>

  <div class="p-6 space-y-6">
    <!-- Loading Spinner -->
    <div *ngIf="isLoading" class="flex justify-center items-center h-screen">
      <mat-spinner></mat-spinner>
    </div>

    <!-- Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4" *ngIf="!isLoading">
      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Active Orders</mat-card-title>
          <mat-icon class="text-gray-500">people</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold">{{ getTotalOrders() }}</div>
          <p class="text-xs text-gray-600">Orders in progress</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Pending Orders</mat-card-title>
          <mat-icon class="text-red-500">warning</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-red-500">{{ getPendingOrders() }}</div>
          <p class="text-xs text-gray-600">Need attention</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Today's Revenue</mat-card-title>
          <mat-icon class="text-green-500">attach_money</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-green-500">${{ getTodaysRevenue() | number:'1.2-2' }}</div>
          <p class="text-xs text-gray-600">Total sales</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Avg Order Value</mat-card-title>
          <mat-icon class="text-blue-500">trending_up</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-blue-500">${{ getAverageOrderValue() | number:'1.2-2' }}</div>
          <p class="text-xs text-gray-600">Per order</p>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Tabs -->
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="onTabChange($event)" *ngIf="!isLoading">
      <mat-tab [label]="'All Orders (' + getTotalOrders() + ')'">
        <div class="mt-4">
          <mat-card>
            <mat-card-header>
              <mat-card-title>All Orders</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="mat-elevation-z2 rounded-lg overflow-hidden">
                <table mat-table [dataSource]="dataSource" matSort class="w-full">
                  <!-- Order ID Column -->
                  <ng-container matColumnDef="id">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Order ID </th>
                    <td mat-cell *matCellDef="let order"> {{ order.invoiceNumber || '#' + order.id }} </td>
                  </ng-container>

                  <!-- Table Column -->
                  <ng-container matColumnDef="table">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Table </th>
                    <td mat-cell *matCellDef="let order"> Table {{ order.tableNumber || '-' }} </td>
                  </ng-container>

                  <!-- Customer Column -->
                  <ng-container matColumnDef="customer">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Customer </th>
                    <td mat-cell *matCellDef="let order"> {{ order.customerName || 'Anonymous' }} </td>
                  </ng-container>

                  <!-- Items Column -->
                  <ng-container matColumnDef="items">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Items </th>
                    <td mat-cell *matCellDef="let order">
                      {{ getItemsCount(order) }} item(s)
                      <span class="text-xs text-gray-500 block truncate max-w-[150px]" [title]="getItemsSummary(order)">
                        {{ getItemsSummary(order) }}
                      </span>
                    </td>
                  </ng-container>

                  <!-- Total Column -->
                  <ng-container matColumnDef="total">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Total </th>
                    <td mat-cell *matCellDef="let order"> ${{ order.total | number:'1.2-2' }} </td>
                  </ng-container>

                  <!-- Status Column -->
                  <ng-container matColumnDef="status">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Status </th>
                    <td mat-cell *matCellDef="let order" class="p-1">
                      <mat-chip [color]="getStatusColor(order.status)" class="gap-2">
                        <div class="flex items-center gap-2">
                          <mat-icon class="text-base">{{ getStatusIcon(order.status) }}</mat-icon>
                          {{ getDisplayStatus(order.status) }}
                        </div>
                      </mat-chip>
                    </td>
                  </ng-container>

                  <!-- Time Column -->
                  <ng-container matColumnDef="time">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header> Time </th>
                    <td mat-cell *matCellDef="let order"> {{ order.createdAt | date:'short' }} </td>
                  </ng-container>

                  <!-- Actions Column -->
                  <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef> Actions </th>
                    <td mat-cell *matCellDef="let order">
                      <div class="flex gap-1">
                        <button mat-icon-button (click)="openOrderDetail(order); $event.stopPropagation()">
                          <mat-icon>visibility</mat-icon>
                        </button>
                        <button *ngIf="order.status?.toUpperCase() === 'PENDING'" mat-raised-button size="small"
                          color="primary" (click)="updateOrderStatus(order, 'PREPARING'); $event.stopPropagation()"
                          [disabled]="isUpdatingStatus">
                          Start
                        </button>
                        <button *ngIf="order.status?.toUpperCase() === 'PREPARING'" mat-raised-button size="small"
                          color="accent" (click)="updateOrderStatus(order, 'READY'); $event.stopPropagation()"
                          [disabled]="isUpdatingStatus">
                          Ready
                        </button>
                        <button *ngIf="order.status?.toUpperCase() === 'READY'" mat-raised-button size="small"
                          color="primary" (click)="updateOrderStatus(order, 'DELIVERED'); $event.stopPropagation()"
                          [disabled]="isUpdatingStatus">
                          Deliver
                        </button>
                        <!-- Cash is only settled when a person at the bar says the
                             money arrived; the customer cannot mark it paid.
                             The control appears ONLY while a cash payment is
                             actually PENDING. It used to be driven by the
                             order's UNPAID flag, so it also appeared for failed
                             payments and for orders with no payment record —
                             every press answered 409 and nothing happened. -->
                        <button *ngIf="awaitingCash(order)" mat-raised-button size="small"
                          color="accent" (click)="confirmCashReceived(order); $event.stopPropagation()"
                          [disabled]="isUpdatingStatus" title="Confirm the cash was received">
                          Cash received
                        </button>

                        <!-- Settled: what was paid and how, with no button. -->
                        <span *ngIf="isPaid(order)"
                              class="inline-flex items-center gap-1 text-green-700 text-sm font-medium">
                          <mat-icon class="!text-base !w-4 !h-4">check_circle</mat-icon>
                          {{ paidLabel(order) }}
                        </span>

                        <!-- Failed: the reason and when, so the counter knows
                             what happened rather than pressing a dead button. -->
                        <span *ngIf="!isPaid(order) && paymentFailed(order)"
                              class="inline-flex items-center gap-1 text-red-700 text-sm"
                              [title]="failureLabel(order)">
                          <mat-icon class="!text-base !w-4 !h-4">error_outline</mat-icon>
                          {{ failureLabel(order) }}
                        </span>

                        <!-- Cash owed, but nothing recorded the payment. -->
                        <span *ngIf="cashUnrecorded(order)"
                              class="inline-flex items-center gap-1 text-amber-700 text-sm"
                              title="No payment record exists for this order yet">
                          <mat-icon class="!text-base !w-4 !h-4">hourglass_empty</mat-icon>
                          Cash owed — no payment recorded
                        </span>
                      </div>
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="cursor-pointer hover:bg-gray-100"
                    (click)="openOrderDetail(row)"></tr>
                </table>

                <mat-paginator [length]="totalElements" [pageSize]="pageSize" [pageSizeOptions]="[5, 10, 25, 50]"
                  (page)="onPageChange($event)" aria-label="Select page of orders">
                </mat-paginator>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <mat-tab [label]="'Pending (' + getPendingOrders() + ')'">
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ng-container *ngIf="getOrdersByStatus('PENDING').length === 0">
              <div class="col-span-full text-center py-8 text-gray-500">
                <mat-icon class="text-6xl text-gray-300">check_circle</mat-icon>
                <p class="mt-2">No pending orders</p>
              </div>
            </ng-container>
            <mat-card *ngFor="let order of getOrdersByStatus('PENDING')" class="border-l-4 border-red-400">
              <mat-card-header>
                <mat-card-title class="flex items-center justify-between">
                  <span>Table {{ order.tableNumber || '-' }}</span>
                  <mat-chip color="warn">Pending</mat-chip>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="space-y-3">
                <div>
                  <p class="text-sm text-gray-600">Order: {{ order.invoiceNumber || '#' + order.id }}</p>
                  <p class="text-sm text-gray-600">Customer: {{ order.customerName || 'Anonymous' }}</p>
                  <p class="text-sm text-gray-600">Items: {{ getItemsCount(order) }}</p>
                  <p class="font-semibold">Total: ${{ order.total | number:'1.2-2' }}</p>
                </div>
                <div class="space-y-2">
                  <button mat-raised-button color="primary" class="w-full"
                    (click)="updateOrderStatus(order, 'PREPARING')" [disabled]="isUpdatingStatus">
                    Confirm Order
                  </button>
                  <button mat-stroked-button class="w-full" (click)="openOrderDetail(order)">
                    View Details
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab [label]="'Preparing (' + getOrdersByStatus('PREPARING').length + ')'">
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ng-container *ngIf="getOrdersByStatus('PREPARING').length === 0">
              <div class="col-span-full text-center py-8 text-gray-500">
                <mat-icon class="text-6xl text-gray-300">restaurant</mat-icon>
                <p class="mt-2">No orders being prepared</p>
              </div>
            </ng-container>
            <mat-card *ngFor="let order of getOrdersByStatus('PREPARING')" class="border-l-4 border-yellow-400">
              <mat-card-header>
                <mat-card-title class="flex items-center justify-between">
                  <span>Table {{ order.tableNumber || '-' }}</span>
                  <mat-chip color="accent">Preparing</mat-chip>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="space-y-3">
                <div>
                  <p class="text-sm text-gray-600">Order: {{ order.invoiceNumber || '#' + order.id }}</p>
                  <p class="text-sm text-gray-600">Customer: {{ order.customerName || 'Anonymous' }}</p>
                  <p class="text-sm text-gray-600">Items: {{ getItemsCount(order) }}</p>
                  <p class="font-semibold">Total: ${{ order.total | number:'1.2-2' }}</p>
                </div>
                <div class="space-y-2">
                  <button mat-raised-button color="accent" class="w-full" (click)="updateOrderStatus(order, 'READY')"
                    [disabled]="isUpdatingStatus">
                    Mark Ready
                  </button>
                  <button mat-stroked-button class="w-full" (click)="openOrderDetail(order)">
                    View Details
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab [label]="'Ready (' + getOrdersByStatus('READY').length + ')'">
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ng-container *ngIf="getOrdersByStatus('READY').length === 0">
              <div class="col-span-full text-center py-8 text-gray-500">
                <mat-icon class="text-6xl text-gray-300">local_bar</mat-icon>
                <p class="mt-2">No orders ready for delivery</p>
              </div>
            </ng-container>
            <mat-card *ngFor="let order of getOrdersByStatus('READY')" class="border-l-4 border-green-400">
              <mat-card-header>
                <mat-card-title class="flex items-center justify-between">
                  <span>Table {{ order.tableNumber || '-' }}</span>
                  <mat-chip color="primary">Ready</mat-chip>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="space-y-3">
                <div>
                  <p class="text-sm text-gray-600">Order: {{ order.invoiceNumber || '#' + order.id }}</p>
                  <p class="text-sm text-gray-600">Customer: {{ order.customerName || 'Anonymous' }}</p>
                  <p class="text-sm text-gray-600">Items: {{ getItemsCount(order) }}</p>
                  <p class="font-semibold">Total: ${{ order.total | number:'1.2-2' }}</p>
                </div>
                <div class="space-y-2">
                  <button mat-raised-button color="primary" class="w-full"
                    (click)="updateOrderStatus(order, 'DELIVERED')" [disabled]="isUpdatingStatus">
                    Mark Delivered
                  </button>
                  <button mat-stroked-button class="w-full" (click)="openOrderDetail(order)">
                    View Details
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab [label]="'Delivered (' + getOrdersByStatus('DELIVERED').length + ')'">
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ng-container *ngIf="getOrdersByStatus('DELIVERED').length === 0">
              <div class="col-span-full text-center py-8 text-gray-500">
                <mat-icon class="text-6xl text-gray-300">check_circle_outline</mat-icon>
                <p class="mt-2">No delivered orders</p>
              </div>
            </ng-container>
            <mat-card *ngFor="let order of getOrdersByStatus('DELIVERED')" class="border-l-4 border-blue-400">
              <mat-card-header>
                <mat-card-title class="flex items-center justify-between">
                  <span>Table {{ order.tableNumber || '-' }}</span>
                  <mat-chip color="primary">Delivered</mat-chip>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="space-y-3">
                <div>
                  <p class="text-sm text-gray-600">Order: {{ order.invoiceNumber || '#' + order.id }}</p>
                  <p class="text-sm text-gray-600">Customer: {{ order.customerName || 'Anonymous' }}</p>
                  <p class="text-sm text-gray-600">Items: {{ getItemsCount(order) }}</p>
                  <p class="font-semibold">Total: ${{ order.total | number:'1.2-2' }}</p>
                  <p class="text-xs text-gray-400">Delivered at: {{ order.updatedAt | date:'short' }}</p>
                </div>
                <div class="space-y-2">
                  <button mat-stroked-button class="w-full" (click)="openOrderDetail(order)">
                    View Details
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>

  <!-- Footer -->
  <app-footer></app-footer>
</div>